import React from 'react';
import { Box, Typography, Divider, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ActivityLogFieldsFragment } from 'src/types';
import { getActivityLogMessage } from 'src/utils/activity_log_utils';
import { formatTimestamp } from 'src/utils/timestamp_utils';

const StyledActivityLogsDropdown = styled(Box)(({ theme }) => ({
  width: 400,
  maxHeight: 400,
  borderRadius: 10,
  padding: theme.spacing(2),
  boxShadow: '0px 4px 24px 0px rgba(0, 0, 0, 0.14)',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.paper,
  overflowY: 'auto',
}));

const ActivityLogRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(1.5, 0),
}));

const ActivityLogHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: 8,
});

const ActivityLogMessage = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  color: theme.palette.text.primary,
  lineHeight: 1.4,
}));

interface ActivityLogsDropdownProps {
  activityLogs: ActivityLogFieldsFragment[];
  isLoading: boolean;
}

export const ActivityLogsDropdown: React.FC<ActivityLogsDropdownProps> = ({
  activityLogs,
  isLoading,
}) => {
  if (!activityLogs || activityLogs.length === 0) {
    return (
      <StyledActivityLogsDropdown>
        <Typography
          variant='body2'
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
            py: 2,
          }}
        >
          {isLoading ? <CircularProgress /> : 'No activity logs available'}
        </Typography>
      </StyledActivityLogsDropdown>
    );
  }

  return (
    <StyledActivityLogsDropdown>
      {activityLogs.map((log, index) => (
        <React.Fragment key={log.id}>
          <ActivityLogRow>
            <ActivityLogHeader>
              <Typography
                variant='caption'
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                }}
              >
                {log.principalName || 'Unknown User'}
              </Typography>
              <Typography
                variant='caption'
                sx={{
                  color: 'text.secondary',
                }}
              >
                {log.createdAt ? formatTimestamp(log.createdAt) : ''}
              </Typography>
            </ActivityLogHeader>
            <ActivityLogMessage>
              {getActivityLogMessage(log)}
            </ActivityLogMessage>
          </ActivityLogRow>
          {index < activityLogs.length - 1 && <Divider sx={{ my: 0 }} />}
        </React.Fragment>
      ))}
    </StyledActivityLogsDropdown>
  );
};
